// A small accessible menu button: a real <button aria-haspopup="menu" aria-expanded> that opens a
// <ul role="menu"> of role="menuitem" buttons. Escape and an outside press close it; Up/Down,
// Home and End move focus; Tab closes it. No menu primitive is in package.json, so this is local.
//
// Two ways to fill it:
//  - `items`: plain { label, onSelect } entries.
//  - `children`: arbitrary components (a button that opens its own dialog, a link). They stay
//    MOUNTED while the menu is closed, so a keyboard command such as `card.addEvent` that a child
//    registers with useCommand keeps working; only their trigger is hidden. Each child renders its
//    trigger as a direct <button> or <a> and any <dialog> beside it; the menu gives those triggers
//    role="menuitem" and the menu-item look, and leaves dialogs (which sit in the top layer, so
//    the closed menu does not hide them) alone.
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@web/lib/cn";
import { Button } from "./Button";
import { Kbd } from "./Kbd";

export interface MenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  /** Key that also does this ("m"): open in the item and exposed as aria-keyshortcuts. */
  shortcut?: string;
  title?: string;
}

export interface MenuProps {
  /** The trigger's text, and the menu's accessible name. */
  label: string;
  items?: readonly MenuItem[];
  children?: ReactNode;
  /** Open below the trigger (default) or above it (for a footer at the bottom of a card). */
  placement?: "bottom" | "top";
  align?: "start" | "end";
  variant?: "secondary" | "ghost";
  className?: string;
}

/** The look of one menu row; shared by `items` and by child triggers. */
export const MENU_ITEM_CLASS = cn(
  "mm-tap flex h-9 w-full items-center justify-start gap-2 rounded-md border border-transparent",
  "bg-transparent px-2.5 text-left text-sm font-medium whitespace-nowrap text-ink-2",
  "hover:bg-sunk hover:text-ink focus-visible:bg-sunk focus-visible:text-ink",
  "disabled:cursor-not-allowed disabled:opacity-55",
);

// The same look for the direct button/link of each child, and "gone" while the menu is closed.
const CHILD_OPEN = cn(
  "[&>li>:is(button,a)]:flex [&>li>:is(button,a)]:h-9 [&>li>:is(button,a)]:w-full",
  "[&>li>:is(button,a)]:items-center [&>li>:is(button,a)]:justify-start",
  "[&>li>:is(button,a)]:gap-2 [&>li>:is(button,a)]:rounded-md",
  "[&>li>:is(button,a)]:border-transparent [&>li>:is(button,a)]:bg-transparent",
  "[&>li>:is(button,a)]:px-2.5 [&>li>:is(button,a)]:text-left [&>li>:is(button,a)]:text-sm",
  "[&>li>:is(button,a)]:font-medium [&>li>:is(button,a)]:text-ink-2",
  "[&>li>:is(button,a)]:hover:bg-sunk [&>li>:is(button,a)]:hover:text-ink",
  "[&>li>:is(button,a)]:focus-visible:bg-sunk",
);
const CHILD_CLOSED = "[&>li>:not(dialog)]:hidden";

const ITEM_SELECTOR = ":scope > li > :is(button, a)";

function menuItems(popup: HTMLElement | null): HTMLElement[] {
  if (!popup) return [];
  return [...popup.querySelectorAll<HTMLElement>(ITEM_SELECTOR)].filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-disabled") !== "true",
  );
}

export function Menu({
  label,
  items = [],
  children,
  placement = "bottom",
  align = "end",
  variant = "secondary",
  className,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLUListElement>(null);
  const keepMounted = children !== undefined && children !== null && children !== false;

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Child triggers are the children's own <button>/<a>: give them the menu-item role.
  useEffect(() => {
    const popup = popupRef.current;
    if (!popup) return;
    const apply = () => {
      for (const el of popup.querySelectorAll<HTMLElement>(ITEM_SELECTOR)) {
        if (el.getAttribute("role") !== "menuitem") el.setAttribute("role", "menuitem");
        if (el.getAttribute("tabindex") !== "-1") el.setAttribute("tabindex", "-1");
      }
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(popup, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open, keepMounted]);

  // Opening puts focus on the first item; closing by Escape/Tab/select is handled where it happens.
  useEffect(() => {
    if (open) menuItems(popupRef.current)[0]?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function move(step: 1 | -1 | "first" | "last") {
    const list = menuItems(popupRef.current);
    if (!list.length) return;
    const at = list.indexOf(document.activeElement as HTMLElement);
    const next =
      step === "first"
        ? 0
        : step === "last"
          ? list.length - 1
          : (at + step + list.length) % list.length;
    list[next]?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    // A dialog opened from a child owns its own keys.
    if (target.closest("dialog")) return;
    const onTrigger = target === triggerRef.current;
    const handled = () => {
      event.preventDefault();
      event.stopPropagation();
    };
    if (!open) {
      if (onTrigger && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        handled();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case "Escape":
        handled();
        close(true);
        break;
      case "Tab":
        setOpen(false);
        break;
      case "ArrowDown":
        handled();
        move(onTrigger ? "first" : 1);
        break;
      case "ArrowUp":
        handled();
        move(onTrigger ? "last" : -1);
        break;
      case "Home":
        handled();
        move("first");
        break;
      case "End":
        handled();
        move("last");
        break;
    }
  }

  return (
    <div ref={rootRef} className={cn("relative inline-flex", className)} onKeyDown={onKeyDown}>
      <Button
        ref={triggerRef}
        size="sm"
        variant={variant}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open || keepMounted ? id : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <ChevronDown aria-hidden className="size-3.5" />
      </Button>
      {open || keepMounted ? (
        <ul
          ref={popupRef}
          id={id}
          role={open ? "menu" : undefined}
          aria-label={open ? label : undefined}
          className={cn(
            "list-none",
            open
              ? cn(
                  "absolute z-30 flex min-w-52 flex-col gap-0.5 rounded-lg border border-line-strong",
                  "bg-paper p-1 shadow-soft",
                  placement === "top" ? "bottom-full mb-1" : "top-full mt-1",
                  align === "end" ? "right-0" : "left-0",
                  CHILD_OPEN,
                )
              : // Closed but mounted: out of the way, while children keep their commands and
                // dialogs (dialogs render in the top layer, so this box does not hide them).
                cn("absolute size-0 overflow-hidden", CHILD_CLOSED),
          )}
          // A press on an item (not inside a dialog it opened) closes the menu.
          onClick={(event) => {
            const target = event.target as HTMLElement;
            if (target.closest("dialog")) return;
            const trigger = target.closest("button, a");
            if (trigger && trigger.parentElement?.parentElement === popupRef.current) close(true);
          }}
        >
          {open
            ? items.map((item) => (
                <li key={item.label} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    disabled={item.disabled}
                    title={item.title}
                    aria-keyshortcuts={item.shortcut}
                    className={MENU_ITEM_CLASS}
                    onClick={item.onSelect}
                  >
                    {item.icon}
                    <span className="min-w-0 flex-1">{item.label}</span>
                    {item.shortcut ? <Kbd>{item.shortcut.toUpperCase()}</Kbd> : null}
                  </button>
                </li>
              ))
            : null}
          {keepMounted
            ? // Each child sits in its own <li role="none"> so its trigger is a direct item.
              wrapChildren(children)
            : null}
        </ul>
      ) : null}
    </div>
  );
}

function wrapChildren(children: ReactNode): ReactNode {
  return Children.toArray(children).map((child, i) => (
    <li key={isValidElement(child) && child.key !== null ? child.key : i} role="none">
      {child}
    </li>
  ));
}
